import { zodResolver } from "@hookform/resolvers/zod"
import { useEffect } from "react"
import { Controller, useForm } from "react-hook-form"
import { useShallow } from "zustand/react/shallow"

import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { CheckboxGroup } from "@/components/ui/checkbox"
import { Input, Label } from "@/components/ui/input"
import { SectionFooter } from "@/components/ui/section-footer"
import { SectionHeader } from "@/components/ui/section-header"
import { SectionLabel } from "@/components/ui/section-label"

import { useFormTabsStore } from "../../components"
import { useCheckinForm } from "../../stores/useCheckinFormStore"
import type { AccountProps } from "../../types"
import { SchoolContact } from "../fields"

import { equipmentOptions, facilitiesSchema } from "./facilities.schema"

interface IFacilities {
  account: AccountProps
  bookingContact: string
  readOnly?: boolean
}

const Facilities: React.FC<IFacilities> = ({
  account,
  bookingContact,
  readOnly = false,
}) => {
  const defaultData = useCheckinForm(
    useShallow((s) => s.getDefaultValues(facilitiesSchema.keyof().options)),
  )

  const updateForm = useCheckinForm((s) => s.updateForm)

  const {
    control,
    handleSubmit,
    formState: { isValid, isSubmitted },
    subscribe,
  } = useForm({
    resolver: readOnly ? undefined : zodResolver(facilitiesSchema),
    mode: "onSubmit",
    reValidateMode: "onBlur",
    defaultValues: {
      available_facilities: defaultData.available_facilities || [],
      possible_disturbances: defaultData.possible_disturbances,
      on_the_day_contact: defaultData.on_the_day_contact || bookingContact,
    },
    disabled: readOnly,
  })
  const onNext = useFormTabsStore((s) => s.onNext)
  const handleBack = useFormTabsStore((s) => s.onBack)
  const setTabState = useFormTabsStore((s) => s.setStatus)

  useEffect(() => {
    // make sure to unsubscribe;
    const callback = subscribe({
      formState: {
        values: true,
      },
      callback: ({ values }) => {
        updateForm(values)
      },
    })

    return () => callback()
  }, [subscribe, updateForm])

  const handleNext = handleSubmit(
    (data) => {
      setTabState("complete")
      updateForm(data)
      onNext()
    },
    () => {
      setTabState("error")
    },
  )

  return (
    <form onSubmit={handleNext} noValidate>
      <div className="flex flex-col gap-6">
        <SectionHeader.Root>
          <SectionHeader.Group>
            <div className="flex flex-1 flex-col justify-center gap-0.5 self-stretch">
              <SectionHeader.Heading>School Facilities</SectionHeader.Heading>
              <SectionHeader.Subheading>
                What's available for the program
              </SectionHeader.Subheading>
            </div>
          </SectionHeader.Group>
        </SectionHeader.Root>

        <div className="flex flex-col gap-5">
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(200px,280px)_minmax(400px,512px)] lg:gap-8">
            <SectionLabel.Root
              size="sm"
              title="Available Equipment"
              className="max-lg:hidden"
            />
            <Controller
              control={control}
              name="available_facilities"
              render={({ field: { value, onChange, disabled } }) => (
                <div>
                  <Label className="pb-2 lg:hidden">
                    What facilities will be available to batyr staff
                  </Label>
                  <CheckboxGroup
                    aria-label="What facilities will be available to batyr staff"
                    value={value}
                    onChange={onChange}
                    isDisabled={disabled}
                  >
                    {equipmentOptions.map((item) => (
                      <Checkbox
                        key={item.value}
                        value={item.value}
                        label={item.label}
                        size="sm"
                      />
                    ))}
                  </CheckboxGroup>
                </div>
              )}
            />
          </div>
          <hr className="bg-border-secondary h-px w-full border-none" />
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(200px,280px)_minmax(400px,512px)] lg:gap-8">
            <SectionLabel.Root
              size="sm"
              title="Potential Disruptions"
              className="max-lg:hidden"
            />
            <Controller
              control={control}
              name="possible_disturbances"
              render={({ field: { value, onChange, onBlur, disabled } }) => (
                <div>
                  <Label className="pb-1.5 lg:hidden">
                    Would there be anything that could impact the delivery of
                    the program
                  </Label>
                  <Input
                    placeholder="e.g. bells, music classes nearby"
                    hint="I.e. bells, music classes nearby"
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    isDisabled={disabled}
                  />
                </div>
              )}
            />
          </div>
          <hr className="bg-border-secondary h-px w-full border-none" />
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(200px,280px)_minmax(400px,512px)] lg:gap-8">
            <SectionLabel.Root
              size="sm"
              title="On-the-day Contact"
              className="max-lg:hidden"
            />
            <Controller
              control={control}
              name="on_the_day_contact"
              render={({
                field: { value, onChange, onBlur, disabled },
                fieldState: { error },
              }) => (
                <SchoolContact
                  value={value}
                  onChange={onChange}
                  accountId={account.id}
                  onBlur={onBlur}
                  error={error}
                  isDisabled={disabled}
                  isRequired
                />
              )}
            />
          </div>
        </div>
        <SectionFooter.Root>
          <SectionFooter.Actions>
            <Button color="secondary" onPress={handleBack}>
              Back
            </Button>
            <Button
              isDisabled={isSubmitted && !isValid}
              color="primary"
              type="submit"
            >
              Next
            </Button>
          </SectionFooter.Actions>
        </SectionFooter.Root>
      </div>
    </form>
  )
}

export default Facilities
